import { useState, useEffect } from 'react';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import Badge from './Shared/Badge';
import { fetchInventory, saveInventoryItem, deleteInventoryItem } from '../services/inventoryService';

const INVENTORY_CATEGORIES = ['Gel Color','Top/Base Coat','Builder Gel','Consumables','Tools','Skincare','Other'];

export default function InventoryManager() {
  const emptyForm = { name: '', category: 'Gel Color', qty: '0', low_stock_threshold: '3', is_hema_free: false, notes: '' };
  const [items,          setItems]          = useState([]);
  const [form,           setForm]           = useState(emptyForm);
  const [editId,         setEditId]         = useState(null);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try { const data = await fetchInventory(); setItems(data); } catch {}
  };

  const resetForm = () => { setForm(emptyForm); setEditId(null); };

  const saveItem = async () => {
    if (!form.name.trim()) return alert('Please enter an item name.');
    const payload = { name: form.name.trim(), category: form.category, qty: Number(form.qty) || 0, low_stock_threshold: Number(form.low_stock_threshold) || 3, is_hema_free: form.is_hema_free, notes: form.notes.trim() };
    try { await saveInventoryItem(payload, editId); resetForm(); loadItems(); }
    catch (err) { alert('Could not save: ' + err.message); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ name: item.name || '', category: item.category || 'Gel Color', qty: String(item.qty ?? 0), low_stock_threshold: String(item.low_stock_threshold ?? 3), is_hema_free: Boolean(item.is_hema_free), notes: item.notes || '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try { await deleteInventoryItem(id); loadItems(); if (editId === id) resetForm(); }
    catch (err) { alert('Could not delete: ' + err.message); }
  };

  const getStockStatus = (item) => {
    const qty = Number(item.qty ?? 0); const thr = Number(item.low_stock_threshold ?? 3);
    if (qty === 0) return { label: 'Out of Stock', color: P.red };
    if (qty <= thr) return { label: 'Low Stock', color: P.amber };
    return null;
  };

  const alertItems    = items.filter(item => getStockStatus(item) !== null);
  const filteredItems = items.filter(item => {
    const matchesSearch   = [item.name, item.category, item.notes].join(' ').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>Inventory</h2>
          <p style={{ fontSize: '13px', color: P.muted }}>{items.length} items · {alertItems.length} alerts</p>
        </div>
        {editId && <Btn size="sm" variant="ghost" onClick={resetForm}>Cancel Edit</Btn>}
      </div>
      {alertItems.length > 0 && (
        <div style={{ background: '#FFF8EC', border: `1.5px solid ${P.amber}60`, borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
          <p style={{ fontWeight: 700, fontSize: '13px', color: P.amber, marginBottom: '8px' }}>⚠️ Stock Alerts ({alertItems.length})</p>
          {alertItems.map(item => {
            const status = getStockStatus(item);
            return (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${P.border}` }}>
                <span style={{ fontSize: '13px', color: P.text }}>{item.name} <span style={{ color: P.muted, fontSize: '11px' }}>({item.category})</span></span>
                <span style={{ background: status.color + '20', color: status.color, padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{status.label} · {item.qty}</span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ ...cardStyle, border: `1.5px solid ${P.peach}` }}>
        <h3 style={{ fontSize: '18px', color: P.deep, marginBottom: '12px' }}>{editId ? 'Edit Inventory Item' : 'Add Inventory Item'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div><label style={labelStyle}>Item Name *</label><input style={inputStyle} placeholder="e.g., Builder Gel Clear" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div><label style={labelStyle}>Category</label><select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>{INVENTORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
          <div><label style={labelStyle}>Qty</label><input type="number" min="0" style={inputStyle} value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} /></div>
          <div><label style={labelStyle}>Low Stock Alert (qty ≤)</label><input type="number" min="1" style={inputStyle} value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '14px', fontWeight: 700, color: P.deep }}>
          <input type="checkbox" checked={form.is_hema_free} onChange={e => setForm({ ...form, is_hema_free: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: P.rose }} />
          HEMA-free product
        </label>
        <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Notes</label><input style={inputStyle} placeholder="Shade, supplier, reorder reminder..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        <Btn onClick={saveItem} style={{ width: '100%' }}>{editId ? 'Save Changes' : '+ Add Item'}</Btn>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', marginBottom: '14px' }}>
        <input style={inputStyle} placeholder="Search inventory..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        <select style={inputStyle} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="All">All Categories</option>
          {INVENTORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {filteredItems.length === 0 ? (
        <p style={{ textAlign: 'center', color: P.muted, padding: '30px' }}>No inventory items found.</p>
      ) : filteredItems.map(item => {
        const stockStatus = getStockStatus(item);
        return (
          <div key={item.id} style={{ ...cardStyle, borderLeft: stockStatus ? `4px solid ${stockStatus.color}` : `4px solid ${P.green}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
              <div>
                <h4 style={{ color: P.deep, marginBottom: '6px' }}>{item.name}</h4>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {item.category && <Badge color={P.rose}>{item.category}</Badge>}
                  {item.is_hema_free && <Badge color={P.green}>HEMA-free</Badge>}
                  {stockStatus
                    ? <span style={{ background: stockStatus.color + '20', color: stockStatus.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{stockStatus.label} · {item.qty ?? 0}</span>
                    : <span style={{ background: P.green + '20', color: P.green, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>In Stock · {item.qty ?? 0}</span>}
                </div>
                {item.notes && <p style={{ color: P.muted, fontSize: '13px', marginTop: '8px' }}>{item.notes}</p>}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <Btn size="sm" variant="secondary" onClick={() => startEdit(item)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => handleDelete(item.id)}>Delete</Btn>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
